import { useEffect, useReducer, useState } from 'react';

import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { createContainer } from 'unstated-next';
import { v4 as uuid } from 'uuid';

import { UserRole } from './constants/user-roles';
import { AUTH_ME } from '../../config/apiPaths';
import { type AuthMeResponse } from '../../types/apiTypes';
import { createOauthState } from '../../utils/oauthState';
import { getUrls } from '../../utils/url';

interface AuthState {
    isLoading: boolean;
    isLoggedIn: boolean;
    isAdmin: boolean;
    isSuperAdmin: boolean;
    isInstructor: boolean;
    isStudent: boolean;
    userToken: string;
    hasCustomerSupportMode: boolean;
    user: AuthMeResponse | null;
}

const defaultState: AuthState = {
    isLoading: true,
    isLoggedIn: false,
    isAdmin: false,
    isSuperAdmin: false,
    isInstructor: false,
    isStudent: false,
    userToken: '',
    user: null,
    hasCustomerSupportMode: false
};

const reducer = (_: AuthState, action) => {
    console.log('blah');
    return {
        isLoading: false,
        isLoggedIn: true,
        isAdmin: action.isAdmin,
        isSuperAdmin: action.isSuperAdmin,
        userToken: action.token,
        isInstructor: action.isInstructor,
        isStudent: action.isStudent,
        user: action.user,
        hasCustomerSupportMode: action.hasCustomerSupportMode
    };
};

const useAdminAuth = () => {
    const [state, dispatch] = useReducer(reducer, defaultState);

    const [authMode, setAuthMode] = useState<string>(window.localStorage.getItem('authMode') || 'customerSupport');
    const [showDemoContent, setShowDemoContent] = useState<boolean>(
        window.localStorage.getItem('showDemoContent') === 'true'
    );

    const history = useHistory();
    const location = useLocation();

    const { t } = useTranslation('portal');
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (state.isLoggedIn) return;

        const { api, vanityUrl } = getUrls();

        const redirectToLogin = (token?: string) => {
            const search = new URLSearchParams(location.search);

            const showOrgType = search.get('showOrgType') === 'true';
            const showExperimentalSettings = search.get('showExperimentalSettings') === 'true';

            const newState = createOauthState({
                user_uuid: token || uuid(),
                redirect: vanityUrl + location.pathname,
                redirect_absolute: true,
                showOrgType: showOrgType || undefined,
                showExperimentalSettings: showExperimentalSettings || undefined
            });

            const params = new URLSearchParams({
                state: JSON.stringify(newState)
            });

            history.push(`/login?${params.toString()}`);
        };

        const getAuth = async (token: string) => {
            try {
                const { data } = await axios.get<AuthMeResponse>(`${api}${AUTH_ME}`, { headers: { token } });
                return data;
            } catch (err) {
                console.error(err);
                return false;
            }
        };

        const getUser = async () => {
            const token = localStorage.getItem('user_uuid');

            if (!token) {
                return redirectToLogin();
            }

            try {
                const user = await getAuth(token);

                if (!user) {
                    return redirectToLogin(token);
                }

                const isSuperAdmin = user.roles.includes(UserRole.SuperAdmin);
                const isAdmin = user.roles.includes(UserRole.Admin) || isSuperAdmin;
                const isInstructor = user.roles.includes(UserRole.Teacher);
                const isStudent = user.roles.includes(UserRole.Student) || user.roles.length === 0;
                const hasCustomerSupportMode = user.roles.includes(UserRole.CustomerSupport);

                dispatch({
                    type: 'login',
                    isAdmin,
                    isSuperAdmin,
                    isInstructor,
                    isStudent,
                    token,
                    user,
                    hasCustomerSupportMode
                });
            } catch (error) {
                enqueueSnackbar(t('error.userError'), { variant: 'error' });
            }
        };

        getUser();
    }, [enqueueSnackbar, history, location.pathname, location.search, state.isLoggedIn, t]);

    return {
        isLoading: state.isLoading,
        isLoggedIn: state.isLoggedIn,
        isAdmin: state.isAdmin,
        isSuperAdmin: state.isSuperAdmin,
        isInstructor: state.isInstructor,
        isStudent: state.isStudent,
        uuid: state.userToken,
        authMode,
        setAuthMode,
        showDemoContent,
        setShowDemoContent,
        hasCustomerSupportMode: state.hasCustomerSupportMode
    };
};

const AdminAuthContainer = createContainer(useAdminAuth);

export default AdminAuthContainer;
